import offlineDb from "../db/appDb";
import { createMonteurScopeKey } from "./monteurOfflineService";

function toSafeScopeKey(scopeKey) {
  return String(scopeKey || "").trim();
}

function toSafeCacheKey(cacheKey) {
  return String(cacheKey || "").trim();
}

function toSafeOpnameId(opnameId) {
  return String(opnameId || "").trim();
}

function toSafeDraftId(draftId) {
  return String(draftId || "").trim();
}

function toSafePhotoId(photoId) {
  return String(photoId || "").trim();
}

function createUpdatedAt() {
  return new Date().toISOString();
}

async function getScopedCacheRecord(tableName, { scopeKey, cacheKey }) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  const safeCacheKey = toSafeCacheKey(cacheKey);
  if (!safeScopeKey || !safeCacheKey) return null;
  return offlineDb.table(tableName).get([safeScopeKey, safeCacheKey]);
}

async function setScopedCacheRecord(tableName, { scopeKey, cacheKey, payload }) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  const safeCacheKey = toSafeCacheKey(cacheKey);
  if (!safeScopeKey || !safeCacheKey) return null;

  const record = {
    scopeKey: safeScopeKey,
    cacheKey: safeCacheKey,
    payload: Array.isArray(payload) ? payload : [],
    updatedAt: createUpdatedAt(),
  };

  await offlineDb.table(tableName).put(record);
  return record;
}

export { createMonteurScopeKey };

export async function getCachedOpnames({ scopeKey, cacheKey = "mine" }) {
  return getScopedCacheRecord("opnames", { scopeKey, cacheKey });
}

export async function setCachedOpnames({ scopeKey, cacheKey = "mine", payload }) {
  return setScopedCacheRecord("opnames", { scopeKey, cacheKey, payload });
}

export async function getCachedOpnameDetail({ scopeKey, opnameId }) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  const safeOpnameId = toSafeOpnameId(opnameId);
  if (!safeScopeKey || !safeOpnameId) return null;
  return offlineDb.opnameDetails.get([safeScopeKey, safeOpnameId]);
}

export async function setCachedOpnameDetail({ scopeKey, opnameId, payload }) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  const safeOpnameId = toSafeOpnameId(opnameId);
  if (!safeScopeKey || !safeOpnameId) return null;

  const record = {
    scopeKey: safeScopeKey,
    opnameId: safeOpnameId,
    payload: payload || null,
    updatedAt: createUpdatedAt(),
  };

  await offlineDb.opnameDetails.put(record);
  return record;
}

export async function getCachedLookupClients({ scopeKey, cacheKey = "all" }) {
  return getScopedCacheRecord("lookupClients", { scopeKey, cacheKey });
}

export async function setCachedLookupClients({ scopeKey, cacheKey = "all", payload }) {
  return setScopedCacheRecord("lookupClients", { scopeKey, cacheKey, payload });
}

export async function getCachedLookupProjects({ scopeKey, cacheKey = "all" }) {
  return getScopedCacheRecord("lookupProjects", { scopeKey, cacheKey });
}

export async function setCachedLookupProjects({ scopeKey, cacheKey = "all", payload }) {
  return setScopedCacheRecord("lookupProjects", { scopeKey, cacheKey, payload });
}

export async function getCachedLookupWorkorders({ scopeKey, cacheKey = "all" }) {
  return getScopedCacheRecord("lookupWorkorders", { scopeKey, cacheKey });
}

export async function setCachedLookupWorkorders({ scopeKey, cacheKey = "all", payload }) {
  return setScopedCacheRecord("lookupWorkorders", { scopeKey, cacheKey, payload });
}

export function createOpnameDraftId(opnameId) {
  const safeOpnameId = toSafeOpnameId(opnameId);
  return safeOpnameId ? `opname:${safeOpnameId}` : "opname:new";
}

export async function getDraftOpname({ scopeKey, draftId }) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  const safeDraftId = toSafeDraftId(draftId);
  if (!safeScopeKey || !safeDraftId) return null;
  return offlineDb.draftOpnames.get([safeScopeKey, safeDraftId]);
}

export async function setDraftOpname({ scopeKey, draftId, payload, meta = {} }) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  const safeDraftId = toSafeDraftId(draftId);
  if (!safeScopeKey || !safeDraftId) return null;

  const record = {
    scopeKey: safeScopeKey,
    draftId: safeDraftId,
    payload: payload || null,
    meta,
    updatedAt: createUpdatedAt(),
  };

  await offlineDb.draftOpnames.put(record);
  return record;
}

export async function deleteDraftOpname({ scopeKey, draftId }) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  const safeDraftId = toSafeDraftId(draftId);
  if (!safeScopeKey || !safeDraftId) return 0;
  return offlineDb.draftOpnames.delete([safeScopeKey, safeDraftId]);
}

export async function putPendingOpnamePhoto({
  scopeKey,
  photoId,
  entityId,
  blob,
  fileName,
  mimeType,
  size,
}) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  const safePhotoId = toSafePhotoId(photoId);
  if (!safeScopeKey || !safePhotoId || !blob) return null;

  const record = {
    scopeKey: safeScopeKey,
    photoId: safePhotoId,
    entityId: toSafeOpnameId(entityId),
    blob,
    fileName: String(fileName || "").trim(),
    mimeType: String(mimeType || "").trim(),
    size: Number(size || blob?.size || 0) || 0,
    updatedAt: createUpdatedAt(),
  };

  await offlineDb.opnamePhotoBlobs.put(record);
  return record;
}

export async function getPendingOpnamePhoto({ scopeKey, photoId }) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  const safePhotoId = toSafePhotoId(photoId);
  if (!safeScopeKey || !safePhotoId) return null;
  return offlineDb.opnamePhotoBlobs.get([safeScopeKey, safePhotoId]);
}

export async function deletePendingOpnamePhoto({ scopeKey, photoId }) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  const safePhotoId = toSafePhotoId(photoId);
  if (!safeScopeKey || !safePhotoId) return 0;
  return offlineDb.opnamePhotoBlobs.delete([safeScopeKey, safePhotoId]);
}
